import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCheck, FiX, FiDollarSign, FiUsers, FiPhone, FiSettings, 
  FiClock, FiTrendingUp, FiShield, FiZap, FiTarget, FiStar, 
  FiArrowRight, FiPlay, FiInfo, FiCalendar, FiCode 
} = FiIcons;

const RetellVsThoughtly = () => {
  const scrollToGetStarted = () => {
    const element = document.querySelector('[data-section="get-started"]');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Load Sayify script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://resource.sayify.pro/cdn/widget.js";
    script.async = true;
    script.dataset.slug = "customer-inquiry";
    script.dataset.mode = "inline-form";
    script.dataset.baseUrl = "https://crcnu.sayify.pro";
    script.dataset.label = "Feedback";
    script.dataset.color = "#C86A4A";
    script.dataset.position = "bottom-right";
    script.dataset.description = "Share your feedback by voice";
    script.dataset.inlineHeight = "600";
    script.dataset.autoHeight = "true";
    script.dataset.container = "sayify-widget-container-comparison";
    
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Retell AI vs. Thoughtly: A Comprehensive{' '}
              <span className="text-blue-600">AI Voice Platform</span> Comparison
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-8">
              An objective analysis of two leading AI voice platforms to help businesses make informed decisions.
              As an official Retell AI Creator Partner, we provide expert insights into both platforms' capabilities,
              target markets, and implementation approaches.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+13322324531"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition-colors duration-300 flex items-center gap-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                Schedule Expert Consultation
              </a>
              <button
                onClick={scrollToGetStarted}
                className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-blue-600 transition-colors duration-300"
              >
                View Detailed Comparison
              </button>
            </div>
          </motion.div>

          {/* Creator Partner Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg border border-blue-200">
              <SafeIcon icon={FiStar} className="w-5 h-5 text-blue-600" />
              <span className="text-gray-700 font-medium">Official Retell AI Creator Partner</span>
              <SafeIcon icon={FiStar} className="w-5 h-5 text-blue-600" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Understanding the AI Voice Platform Landscape
            </h2>
            <div className="max-w-4xl mx-auto text-gray-700 space-y-6 text-lg leading-relaxed">
              <p>
                The AI voice agent market offers diverse platforms serving different business needs and market segments.
                Both <strong>Retell AI</strong> and <strong>Thoughtly</strong> provide voice agent capabilities, but their approaches,
                target markets, and implementation strategies differ significantly.
              </p>
              <p>
                As a <strong>Retell AI Creator Partner</strong>, we have extensive experience implementing both platforms
                across various industries. This comparison provides objective insights into each platform's strengths,
                target audiences, and ideal use cases to help businesses make informed decisions.
              </p>
              <p>
                Our analysis focuses on practical considerations including pricing transparency, implementation timelines,
                technical capabilities, and long-term scalability to provide a comprehensive evaluation framework for decision-makers.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform Overview - Thoughtly */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Thoughtly AI: Enterprise-Focused Voice Solutions
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  Thoughtly AI positions itself as an enterprise-grade voice agent platform, focusing on large organizations
                  with substantial budgets and complex requirements. The platform emphasizes custom implementations
                  and white-glove service delivery.
                </p>
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiInfo} className="w-5 h-5" />
                    Thoughtly's Market Positioning
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-blue-700 font-semibold">Enterprise Market Focus:</span>
                        <p className="text-blue-700 text-sm mt-1">Targets large organizations with significant annual technology budgets and complex integration requirements.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-blue-700 font-semibold">Custom Implementation Approach:</span>
                        <p className="text-blue-700 text-sm mt-1">Emphasizes bespoke solutions with dedicated implementation teams and extended deployment timelines.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-blue-700 font-semibold">High-Touch Service Model:</span>
                        <p className="text-blue-700 text-sm mt-1">Provides comprehensive professional services with dedicated account management and support teams.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-blue-700 font-semibold">Consultative Sales Process:</span>
                        <p className="text-blue-700 text-sm mt-1">Utilizes extensive discovery and proposal phases to develop custom pricing and implementation plans.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Thoughtly's Target Market</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">Fortune 500 companies</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">Large healthcare systems</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">Major financial institutions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">Enterprise technology companies</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2">Investment Requirements:</h4>
                  <p className="text-sm text-gray-700">
                    Thoughtly typically requires substantial annual commitments and extended implementation timelines,
                    making it suitable for organizations with significant technology budgets and dedicated IT resources.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Comparison Table and Other Sections ... */}
      
      {/* Final Recommendation & Sayify CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Platform Recommendation: Choose Based on Your Needs
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
              Both <strong>Retell AI</strong> and <strong>Thoughtly</strong> serve their respective markets effectively.
              Your choice should align with your organization's budget, timeline, technical requirements, and service preferences.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-blue-200 mb-4">Choose Retell AI If:</h3>
                <ul className="text-blue-100 text-left space-y-2">
                  <li>• You need rapid deployment (24-48 hours)</li>
                  <li>• You prefer transparent, usage-based pricing</li>
                  <li>• You want developer-friendly API access</li>
                  <li>• You require scalable, flexible solutions</li>
                  <li>• You value production-ready reliability</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-blue-200 mb-4">Choose Thoughtly If:</h3>
                <ul className="text-blue-100 text-left space-y-2">
                  <li>• You have substantial enterprise budgets</li>
                  <li>• You prefer comprehensive service packages</li>
                  <li>• You can accommodate extended timelines</li>
                  <li>• You require extensive customization</li>
                  <li>• You value white-glove service delivery</li>
                </ul>
              </div>
            </div>

            {/* Sayify Embed Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="w-full"
              data-section="get-started"
            >
              <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm mb-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Ready to Explore Your Options?
                </h3>
                <p className="text-blue-100 mb-6">
                  As an official <strong>Retell AI Creator Partner</strong>, we provide expert consultation to help you 
                  choose the right platform and ensure successful implementation.
                </p>
                <p className="text-center text-blue-100 mb-8">
                  Experience our technology firsthand by calling our demo line at <a href="tel:+13322324531" className="font-bold text-white hover:text-blue-200 transition-colors">+1 332-232-4531</a>
                </p>
                
                <div id="sayify-widget-container-comparison" className="bg-white rounded-xl shadow-inner overflow-hidden min-h-[600px]">
                  {/* Sayify Widget will inject here */}
                </div>
              </div>
            </motion.div>

            <p className="text-blue-200 text-lg">
              Make an informed decision based on your specific needs, timeline, and budget.
              Both platforms offer value in their respective market segments.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RetellVsThoughtly;